import styled from '@emotion/styled';
import vars from '@variables';
import { H2Atom, PAtom } from '@atoms/text-atoms/text-atoms';
import { hexToRgba } from '@utils/utils';

interface GalleryCarouselWrapperProps {
  anchorId?: string;
  images?: any[];
  colour?: string;
  isFullWidth?: boolean;
  enableLightText?: boolean;
}

const CAROUSEL_HEIGHT_MOBILE = '40vh';
const CAROUSEL_HEIGHT_TABLET = '60vh';
const CAROUSEL_HEIGHT_DESKTOP = '80vh';

export const GalleryCarouselWrapper = styled.div<GalleryCarouselWrapperProps>`
  position: ${({ isFullWidth }) => (isFullWidth ? 'relative' : 'static')};
  left: ${({ isFullWidth }) => (isFullWidth ? '50%' : 'auto')};
  right: ${({ isFullWidth }) => (isFullWidth ? '50%' : 'auto')};
  margin-left: ${({ isFullWidth }) => (isFullWidth ? '-50vw' : 'auto')};
  margin-right: ${({ isFullWidth }) => (isFullWidth ? '-50vw' : 'auto')};
  width: ${({ isFullWidth }) => (isFullWidth ? '100vw' : '100%')};
  margin-top: ${({ anchorId }) => (anchorId ? vars.spacingL : '0px')};
  padding-bottom: 2rem;

  text-align: left;
  ${vars.fontSize8}
  overflow-x: auto;

  max-width: ${({ isFullWidth }) => {
    return isFullWidth ? '100vw' : '100%';
  }};

  ${H2Atom} {
    color: ${({ enableLightText }) => (enableLightText ? vars.white : vars.coreDarkGrey)} !important;
    margin-top: ${vars.spacingM};
    max-width: ${({ isFullWidth }) => (isFullWidth ? '130rem' : '66%')};
    padding-left: ${({ isFullWidth }) => (isFullWidth ? '2.2rem' : '0')};
    padding-right: ${({ isFullWidth }) => (isFullWidth ? '2.2rem' : '0')};
    margin-left: ${({ isFullWidth }) => (isFullWidth ? 'auto' : '0')};
    margin-right: ${({ isFullWidth }) => (isFullWidth ? 'auto' : '0')};
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      margin-top: ${vars.spacingL};
    }
    @media screen and (max-width: 767px) {
      padding-left: ${({ isFullWidth }) => (isFullWidth ? '2.4rem' : '0')};
      padding-right: ${({ isFullWidth }) => (isFullWidth ? '2.4rem' : '0')};
    }
    @media screen and (min-width: 768px) and (max-width: 1300px) {
      padding-left: ${({ isFullWidth }) => (isFullWidth ? '3rem' : '0')};
      padding-right: ${({ isFullWidth }) => (isFullWidth ? '3rem' : '0')};
    }
  }

  ${PAtom} {
    max-width: ${({ isFullWidth }) => (isFullWidth ? '130rem' : '66%')};
    padding-left: ${({ isFullWidth }) => (isFullWidth ? '2.2rem' : '0')};
    padding-right: ${({ isFullWidth }) => (isFullWidth ? '2.2rem' : '0')};
    margin-left: ${({ isFullWidth }) => (isFullWidth ? 'auto' : '0')};
    margin-right: ${({ isFullWidth }) => (isFullWidth ? 'auto' : '0')};
    color: ${({ enableLightText }) => {
      return enableLightText ? vars.white : vars.coreDarkGrey;
    }} !important;

    @media screen and (max-width: 767px) {
      padding-left: ${({ isFullWidth }) => (isFullWidth ? '2.4rem' : '0')};
      padding-right: ${({ isFullWidth }) => (isFullWidth ? '2.4rem' : '0')};
    }
    @media screen and (min-width: 768px) and (max-width: 1300px) {
      padding-left: ${({ isFullWidth }) => (isFullWidth ? '3rem' : '0')};
      padding-right: ${({ isFullWidth }) => (isFullWidth ? '3rem' : '0')};
    }

  .gatsby-image-pos > div {
    max-width: inherit !important;
  }

  .content-container {
    display: flex;
    width: ${({ images }) =>
      images && images.length > 1
        ? vars.doubleImageContentContainerPercentageWidth
        : '100%'};
    justify-content: space-between;
    margin-top: ${vars.spacingM};

    .image-with-caption-wrapper {
      .gatsby-image-wrapper {
        width: 100%;
      }

      width: ${({ images }) =>
        images && images.length > 1 ? vars.mobileImagePercentageWidth : '100%'};

      img {
        width: 100%;
        border-radius: 8px;
      }

      ${PAtom} {
        margin: 0;
        padding-left: ${({ isFullWidth }) => (isFullWidth ? '2rem' : '0')};
        padding-right: ${({ isFullWidth }) => (isFullWidth ? '2rem' : '0')};
        ${vars.fontSize2}
        padding: ${vars.spacingS} 2rem 0 0;
        color: ${({ enableLightText }) => {
          return enableLightText ? vars.white : vars.coreDarkGrey;
        }} !important;

      a {
        display: block;
        text-decoration: none;
        &:hover {
          opacity: 0.8;
        }
      }
    }
  }

  @media screen and (min-width: ${vars.breakPointTenColumn}) {
    .content-container {
      width: 100%;

      .image-with-caption-wrapper {
        width: ${({ images }) =>
          images && images.length > 1
            ? vars.standardDoubleImagePercentageWidth
            : '100%'};

        img {
          width: 100%;
        }
      }
    }
  }

  @media screen and (max-width: ${vars.breakPointTablet}) {
    max-width: ${({ isFullWidth }) => {
      return isFullWidth ? '100vw' : '100%';
    }};
  }
`;

export const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  height: ${CAROUSEL_HEIGHT_MOBILE};
  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    height: ${CAROUSEL_HEIGHT_DESKTOP};
  }
  @media screen and (min-width: ${vars.breakPointTablet}) {
    height: ${CAROUSEL_HEIGHT_TABLET};
  }
`;

export const EmblaViewport = styled.div`
  overflow: hidden;
  width: 100%;
`;

export const EmblaContainer = styled.div<{ isFullWidth?: boolean }>`
  display: flex;
  height: ${CAROUSEL_HEIGHT_MOBILE};
  align-items: center;
  width: ${p => (p.isFullWidth ? '100%' : 'auto')};

  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    height: ${CAROUSEL_HEIGHT_DESKTOP};
  }
  @media screen and (min-width: ${vars.breakPointTablet}) {
    height: ${CAROUSEL_HEIGHT_TABLET};
  }
`;

export const EmblaSlide = styled.div<{
  isSelected: boolean;
  backgroundColor?: string;
}>`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  margin: 0 5px;
  max-width: 80%;
  opacity: 1;
  transform: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${p =>
      p.isSelected
        ? 'transparent'
        : p.backgroundColor
          ? hexToRgba(p.backgroundColor, 0.4)
          : 'rgba(255, 255, 255, 0.6)'};

    pointer-events: none;
  }
`;

export const ImageWrapper = styled.div`
  height: 30vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .gatsby-image-wrapper {
    border-radius: 8px;
  }
  .gatsby-image-wrapper,
  .gatsby-image-wrapper > picture,
  .gatsby-image-wrapper img {
    height: 100% !important;
    width: auto !important;
    object-fit: contain !important;
  }
  @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
    height: 70vh;
  }
  @media screen and (min-width: ${vars.breakPointTablet}) {
    height: 50vh;
  }
`;

export const NavButton = styled.button<{
  isFullWidth?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: ${vars.white};
  border: none;
  border-radius: 2px;
  width: 40px;
  height: 60px;
  padding-block: 0;
  padding-inline: 0;
  z-index: 10;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.2s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  box-shadow:
    0 4px 8px 0 rgba(0, 0, 0, 0.2),
    0 6px 20px 0 rgba(0, 0, 0, 0.19);

  &:hover {
    outline: 2px solid ${vars.white};
    outline-offset: 2px;
  }

  &:focus {
    outline: 2px solid ${vars.white};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LeftButton = styled(NavButton)<{
  isFullWidth?: boolean;
}>`
  left: ${({ isFullWidth }) => (isFullWidth ? '7px' : '0')};
`;

export const RightButton = styled(NavButton)<{
  isFullWidth?: boolean;
}>`
  right: ${({ isFullWidth }) => (isFullWidth ? '7px' : '0')};
`;

export const SlideCount = styled.div<{
  enableLightText?: boolean;
  isFullWidth?: boolean;
}>`
  position: absolute;
  right: 1rem;
  bottom: 0;
  margin-top: 0;
  padding-right: ${({ isFullWidth }) => (isFullWidth ? '2rem' : '0')};
  ${vars.fontSize2};
  font-weight: ${vars.fontWeightBold};
  color: ${({ enableLightText }) => {
    return enableLightText ? vars.white : vars.coreDarkGrey;
  }} !important;
`;

export const BackgroundColor = styled.div<{
  backgroundColor?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  width: 100vw;
  height: 100%;
  margin-left: calc(-50vw + 50%);
  background-color: ${({ backgroundColor }) => backgroundColor};
  overflow: hidden;

  & > div {
    height: 100% !important;
    width: 100% !important;
  }

  img {
    object-fit: cover !important;
    object-position: center !important;
    width: 100% !important;
    height: 100% !important;
  }
`;
